import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Trash2, NotebookPen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SUBJECTS = ["Math", "Sciences", "English", "Language B", "Individuals & Societies", "Design", "Arts", "PHE", "General"];
const COLORS = ["bg-indigo-50 border-indigo-100", "bg-amber-50 border-amber-100", "bg-emerald-50 border-emerald-100", "bg-purple-50 border-purple-100", "bg-rose-50 border-rose-100"];

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", subject: "General", content: "" });

  const load = () => api.get("/notes").then((r) => setNotes(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim()) return;
    await api.post("/notes", form);
    setForm({ title: "", subject: "General", content: "" });
    setOpen(false);
    toast.success("Note saved");
    load();
  };

  const remove = async (id) => { await api.delete(`/notes/${id}`); load(); };

  return (
    <div className="space-y-5">
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid="note-new-btn" className="rounded-xl bg-indigo-600 hover:bg-indigo-700"><Plus className="w-4 h-4 mr-1" /> New note</Button>
        </DialogTrigger>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">New note</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="note-title-input" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" />
            <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
              <SelectTrigger data-testid="note-subject-select" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea data-testid="note-content-input" placeholder="Write your note, key points, formulas..." value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="rounded-xl resize-none" rows={6} />
          </div>
          <DialogFooter>
            <Button data-testid="note-save-btn" onClick={add} className="rounded-xl bg-indigo-600 hover:bg-indigo-700">Save note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="notes-grid">
        {notes.length === 0 && <p className="text-sm text-slate-400">No notes yet. Jot down key points or formulas.</p>}
        {notes.map((n, i) => (
          <motion.div key={n.id} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.04 }}
            className={`rounded-2xl border p-5 ${COLORS[i % COLORS.length]}`}>
            <div className="flex items-start justify-between gap-2">
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/70 text-slate-600">{n.subject}</span>
              <button data-testid={`note-delete-${n.id}`} onClick={() => remove(n.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
            </div>
            <h4 className="font-heading font-semibold text-slate-900 mt-3 flex items-center gap-2"><NotebookPen className="w-4 h-4 text-slate-500" />{n.title}</h4>
            <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap leading-relaxed line-clamp-6">{n.content}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
